import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import Axios from '../../networking/intraceptor';
import { changelog_acknowledge, changelog_important_pending } from '../../networking/urlEndPoints';
import { useCustomSelectors } from '../../services/selectors/allSelectors';
import { showToast } from '../../utils/helper/helper';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import './ImportantChangeLogPopup.css';

// Checked when a page opens, so it never interrupts someone part way through their work. The gap
// stops every click between pages from making a request.
const RECHECK_GAP_MS = 5 * 60 * 1000;

// Writing this tells the person's other open tabs that an update has been acknowledged.
const ACKNOWLEDGED_STORAGE_KEY = 'oatrx-important-update-acknowledged';

// Admins type "•" by hand, and the editor keeps those lines in one block with line breaks and spaces
// around them. Split on the "•" and everything around it, so they can show as a real list.
const TYPED_BULLET = /(?:<br\s*\/?>|<\/?(?:p|div|span)\b[^>]*>|&nbsp;|\s)*(?:•|&bull;|&#8226;)(?:&nbsp;|\s)*/i;

// The lines appear one after another once the popup has landed. Capped so a long update is not slow.
const contentDelay = (position) => ({ animationDelay: `${120 + Math.min(position, 8) * 60}ms` });

const hasText = (html) => html.replace(/<[^>]*>|&nbsp;|\s/g, '') !== '';

const parseDescription = (description) => {
	const [intro, ...items] = String(description ?? '').split(TYPED_BULLET).map((part) => sanitizeHtml(part));
	return { intro: hasText(intro) ? intro : '', items: items.filter(hasText) };
};

const ImportantChangeLogPopup = () => {
	const { pathname } = useLocation();
	const { profileData, teamStatus } = useCustomSelectors();
	// Updates are for pharmacy staff, not our own team. Same rule as the other review popups.
	const isTeamUserRestricted = process.env.NODE_ENV === 'production' && Number(teamStatus) === 1;
	const canShow = Boolean(profileData?.user_id) && !isTeamUserRestricted;
	const [updates, setUpdates] = useState([]);
	const [total, setTotal] = useState(0);
	const [isSaving, setIsSaving] = useState(false);
	const lastCheckedAt = useRef(0);
	const isMounted = useRef(true);
	const acknowledgedIds = useRef(new Set());

	useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	const hasUpdates = updates.length > 0;

	useEffect(() => {
		if (!canShow || hasUpdates || Date.now() - lastCheckedAt.current < RECHECK_GAP_MS) return;
		lastCheckedAt.current = Date.now();

		const load = async () => {
			try {
				const response = await Axios.get(changelog_important_pending);
				const list = response?.data?.data;
				if (isMounted.current && Array.isArray(list) && list.length > 0) {
					setUpdates(list);
					setTotal(list.length);
				}
			} catch (_) {
				// A failed check must not get in the way of the page. The next page open tries again.
				lastCheckedAt.current = 0;
			}
		};

		load();
	}, [pathname, hasUpdates, canShow]);

	// Got it in another tab closes this popup straight away.
	useEffect(() => {
		const handleStorage = (event) => {
			if (event.key !== ACKNOWLEDGED_STORAGE_KEY || !event.newValue) return;
			try {
				const { id } = JSON.parse(event.newValue);
				acknowledgedIds.current.add(id);
				setUpdates((list) => list.filter((update) => update.id !== id));
			} catch (_) {
				// Not a value this popup wrote, so there is nothing to act on.
			}
		};

		window.addEventListener('storage', handleStorage);
		return () => window.removeEventListener('storage', handleStorage);
	}, []);

	// Coming back to this tab, ask again: Got it may have been clicked on another computer.
	useEffect(() => {
		if (!hasUpdates) return undefined;

		const handleVisibilityChange = async () => {
			if (document.visibilityState !== 'visible') return;
			try {
				const response = await Axios.get(changelog_important_pending);
				const list = response?.data?.data;
				if (isMounted.current && Array.isArray(list)) {
					setUpdates(list.filter((update) => !acknowledgedIds.current.has(update.id)));
				}
			} catch (_) {
				// The popup stays as it is. Got it still works.
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	}, [hasUpdates]);

	const current = updates[0];

	const handleAcknowledge = useCallback(async () => {
		if (!current || isSaving) return;
		setIsSaving(true);
		try {
			await Axios.post(changelog_acknowledge(current.id));
			acknowledgedIds.current.add(current.id);
			try {
				localStorage.setItem(ACKNOWLEDGED_STORAGE_KEY, JSON.stringify({ id: current.id, at: Date.now() }));
			} catch (_) {
				// Blocked storage only means other tabs catch up when the person returns to them.
			}
			if (isMounted.current) setUpdates((list) => list.filter((update) => update.id !== current.id));
		} catch (_) {
			showToast('Could not save. Please try again.', 'error');
		} finally {
			if (isMounted.current) setIsSaving(false);
		}
	}, [current, isSaving]);

	if (!canShow || !current) return null;

	const { intro, items } = parseDescription(current.description);

	return (
		<div className="important-update-backdrop fixed top-0 left-0 right-0 bottom-0 z-[70] flex items-center justify-center p-4 overflow-x-hidden overflow-y-auto bg-black bg-opacity-50 print:hidden">
			{/* The key replays the animation when the next update takes over. */}
			<div key={current.id} className="important-update-card relative w-full max-w-3xl bg-white rounded-lg shadow-lg overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="important-update-title">
				<div className="flex items-center gap-4 px-6 py-4 border-b border-gray-200">
					<span className="flex items-center justify-center shrink-0 w-11 h-11 rounded-full bg-amber-100 text-amber-600" aria-hidden="true">
						<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
							<path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
						</svg>
					</span>
					<div className="min-w-0">
						<p className="text-sm font-medium text-amber-700">Important update</p>
						<h3 id="important-update-title" className="text-lg font-semibold leading-snug text-gray-900">{current.title}</h3>
					</div>
				</div>

				{(intro || items.length > 0) && (
					<div className="max-h-[60vh] overflow-y-auto px-6 py-4 text-sm leading-6 text-gray-800 break-words">
						{intro && (
							<div
								className="important-update-content mb-3 last:mb-0 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5"
								style={contentDelay(0)}
								dangerouslySetInnerHTML={{ __html: intro }}
							/>
						)}
						{items.length > 0 && (
							<ul className="list-disc pl-5 space-y-3 marker:text-gray-400">
								{items.map((item, index) => (
									<li key={index} className="important-update-content pl-1" style={contentDelay(index + (intro ? 1 : 0))} dangerouslySetInnerHTML={{ __html: item }} />
								))}
							</ul>
						)}
					</div>
				)}

				<div className="flex items-center justify-between gap-3 px-6 py-3 bg-gray-50 border-t border-gray-200">
					<p className="text-xs text-gray-600">
						{total > 1 ? `Update ${total - updates.length + 1} of ${total}` : ''}
					</p>
					<button
						type="button"
						autoFocus
						onClick={handleAcknowledge}
						disabled={isSaving}
						className="h-[38px] px-6 rounded-lg text-sm font-medium bg-[#004F87] text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400 disabled:opacity-50"
					>
						{isSaving ? 'Saving...' : 'Got it'}
					</button>
				</div>
			</div>
		</div>
	);
};

ImportantChangeLogPopup.displayName = 'ImportantChangeLogPopup';

export default ImportantChangeLogPopup;
